import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Zap, Activity, BarChart2, CheckCircle2, Star, ArrowRight, BookOpen, TrendingUp, Shield, Globe, Moon, ChevronDown, ChevronRight } from 'lucide-react';
export function HomePage() {
  return <div data-theme="light" className="min-h-screen bg-white text-slate-900 font-sans selection:bg-pink-200" data-id="element-3477">
      <div className="relative" data-id="element-3478">
        {/* Dark hero band */}
        <div className="relative bg-[#08060d] overflow-hidden" data-id="element-3479">
          {/* Video Background */}
          <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover opacity-60 mix-blend-screen" src="https://cdn.magicpatterns.com/uploads/piMwX81vswJyKrNc5M1yFy/hero-bg-3.mp4" data-id="element-3480" />
          {/* Dark overlay to ensure text readability */}
          <div className="absolute inset-0 bg-[#08060d]/30 pointer-events-none" data-id="element-3481" />

          {/* Purple radial glow */}
          <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse 800px 400px at 50% 0%, rgba(168,85,247,0.20), transparent 70%)'
        }} data-id="element-3482" />
          {/* Pink offset glow */}
          <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse 600px 300px at 75% 10%, rgba(240,64,122,0.12), transparent 70%)'
        }} data-id="element-3483" />

          {/* Navigation */}
          <nav className="fixed top-6 inset-x-0 z-50 flex justify-center px-6 pointer-events-none" data-id="element-3484">
            <div className="flex items-center justify-between px-6 py-3 w-full max-w-5xl bg-[#0a0f1c]/60 backdrop-blur-xl border border-white/10 rounded-full pointer-events-auto shadow-2xl relative" data-id="element-3485">
              {/* Logo */}
              <Link to="/home" className="flex items-center" data-id="element-3486">
                <img src="https://cdn.magicpatterns.com/uploads/oSMzbV2Nk5xhPzPyjiGQFj/pasted-image.png" alt="Jua logo" className="h-5 object-contain" data-id="element-3487" />
              </Link>

              {/* Centred nav */}
              <div className="hidden md:flex items-center gap-9 absolute left-1/2 -translate-x-1/2" data-id="element-3488">
                <a href="#" className="text-sm text-slate-400 hover:text-white transition-colors" data-id="element-3489">
                  Home
                </a>
                <a href="#" className="text-sm text-slate-400 hover:text-white transition-colors" data-id="element-3490">
                  Athena AI
                </a>
                <a href="#" className="text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center" data-id="element-3491">
                  Product
                  <ChevronDown size={14} className="ml-1 opacity-60" data-id="element-3492" />
                </a>
                <a href="#" className="text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center" data-id="element-3493">
                  Company
                  <ChevronDown size={14} className="ml-1 opacity-60" data-id="element-3494" />
                </a>
              </div>

              {/* Right cluster */}
              <div className="flex items-center gap-6" data-id="element-3495">
                <button aria-label="Toggle theme" className="text-slate-400 hover:text-white transition-colors" data-id="element-3496">
                  <Moon size={16} data-id="element-3497" />
                </button>
                <a href="#" className="hidden md:inline text-sm text-slate-400 hover:text-white transition-colors" data-id="element-3498">
                  Docs
                </a>
                <Link to="/login" className="relative inline-flex" data-id="element-3499">
                  <div className="absolute inset-0 rounded-full blur-md opacity-60 bg-gradient-to-r from-[#f0407a] via-[#c026d3] to-[#a855f7] -z-10" data-id="element-3500" />
                  <div className="relative rounded-full p-[1.5px] bg-gradient-to-r from-[#f0407a] via-[#c026d3] to-[#a855f7]" data-id="element-3501">
                    <div className="rounded-full bg-[#0a0814] px-5 py-2 flex items-center gap-1.5 text-sm font-medium text-white" data-id="element-3502">
                      Access Athena
                      <ChevronRight size={14} className="text-slate-300" data-id="element-3503" />
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </nav>

          {/* Hero Section */}
          <section className="relative pt-[350px] xl:pt-[450px] pb-32 xl:pb-48 px-6 text-center max-w-5xl mx-auto" data-id="element-3504">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 backdrop-blur-xl border border-white/10 shadow-sm mb-10" data-id="element-3505">
              <div className="w-2 h-2 rounded-full bg-pink-500 animate-pulse" data-id="element-3506" />
              <span className="text-sm font-medium text-pink-300" data-id="element-3507">
                Now live: EPT-2 Model v3.0
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl xl:text-8xl font-light tracking-wide mb-8 text-white leading-tight" data-id="element-3508">
              Real-Time Energy <br data-id="element-3509" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#f0407a] to-[#a855f7] italic font-normal" data-id="element-3510">
                Market Intelligence
              </span>
            </h1>
            <p className="text-lg md:text-xl xl:text-2xl text-slate-300 mb-12 max-w-3xl mx-auto leading-relaxed font-light tracking-[0.04em]" data-id="element-3511">
              AI-powered forecasting for European electricity markets. Make
              data-driven decisions with EPT-2, our proprietary weather model.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-3512">
              <Link to="/login" className="relative inline-flex w-full sm:w-auto" data-id="element-3513">
                <div className="absolute inset-0 rounded-full blur-md opacity-60 bg-gradient-to-r from-[#f0407a] via-[#c026d3] to-[#a855f7] -z-10" data-id="element-3514" />
                <div className="relative w-full rounded-full p-[1.5px] bg-gradient-to-r from-[#f0407a] via-[#c026d3] to-[#a855f7]" data-id="element-3515">
                  <div className="rounded-full bg-[#0a0814] px-8 py-3 flex items-center justify-center gap-2 text-base font-medium text-white w-full" data-id="element-3516">
                    Live Demo
                    <ArrowRight size={18} className="text-slate-300" data-id="element-3517" />
                  </div>
                </div>
              </Link>
              <button onClick={() => {
              localStorage.setItem('jua-tour-completed', 'true');
              window.location.hash = '#/athena';
            }} className="inline-flex items-center justify-center h-[52px] px-8 text-base font-semibold rounded-full bg-white/5 backdrop-blur-xl border border-white/15 text-slate-200 hover:bg-white/10 transition-all w-full sm:w-auto" data-id="element-3518">
                Bypass onboarding
              </button>
            </div>
          </section>
        </div>

        {/* Stats Row */}
        <section className="py-16 xl:py-20 px-6 max-w-5xl mx-auto" data-id="element-3519">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 text-center" data-id="element-3520">
            <div data-id="element-3521">
              <div className="text-3xl md:text-4xl font-bold text-slate-900 mb-1" data-id="element-3522">
                94%
              </div>
              <div className="text-xs font-semibold text-slate-500 tracking-wider uppercase" data-id="element-3523">
                Forecast Accuracy
              </div>
            </div>
            <div data-id="element-3524">
              <div className="text-3xl md:text-4xl font-bold text-slate-900 mb-1" data-id="element-3525">
                150+ GW
              </div>
              <div className="text-xs font-semibold text-slate-500 tracking-wider uppercase" data-id="element-3526">
                Assets Managed
              </div>
            </div>
            <div data-id="element-3527">
              <div className="text-3xl md:text-4xl font-bold text-slate-900 mb-1" data-id="element-3528">
                &lt; 15ms
              </div>
              <div className="text-xs font-semibold text-slate-500 tracking-wider uppercase" data-id="element-3529">
                Latency
              </div>
            </div>
            <div data-id="element-3530">
              <div className="text-3xl md:text-4xl font-bold text-slate-900 mb-1" data-id="element-3531">
                99.99%
              </div>
              <div className="text-xs font-semibold text-slate-500 tracking-wider uppercase" data-id="element-3532">
                Uptime
              </div>
            </div>
          </div>
        </section>

        {/* Trusted By */}
        <section className="py-16 xl:py-20 text-center" data-id="element-3533">
          <div className="inline-flex flex-col items-center bg-white/50 backdrop-blur-xl border border-white/70 rounded-2xl px-10 py-8 shadow-[0_2px_16px_rgba(148,163,184,0.08),inset_0_1px_0_rgba(255,255,255,0.80)]" data-id="element-3534">
            <p className="text-xs font-bold text-slate-400 tracking-widest uppercase mb-6" data-id="element-3535">
              Trusted by leading energy companies across Europe
            </p>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-14 text-slate-400" data-id="element-3536">
              {[{
              name: 'EnergyOne',
              icon: Zap
            }, {
              name: 'PowerGrid',
              icon: TrendingUp
            }, {
              name: 'DataFlux',
              icon: BarChart2
            }, {
              name: 'SecureEnergy',
              icon: Shield
            }, {
              name: 'EuroPower',
              icon: Globe
            }].map(company => <div key={company.name} className="flex items-center gap-2 text-base font-semibold" data-id="element-3537">
                  <company.icon size={18} data-id="element-3538" />
                  {company.name}
                </div>)}
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-24 px-6 max-w-6xl mx-auto" data-id="element-3539">
          <div className="text-center mb-16" data-id="element-3540">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-3541">
              Everything you need to trade smarter
            </h2>
            <p className="text-slate-600" data-id="element-3542">
              Comprehensive tools designed for the modern energy desk.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-3543">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100" data-id="element-3544">
              <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center mb-6" data-id="element-3545">
                <Zap size={24} data-id="element-3546" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-3547">
                EPT-2 Forecasting
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-3548">
                Our proprietary AI weather model delivers 94% accuracy,
                outperforming traditional GFS and EC-IFS models.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100" data-id="element-3549">
              <div className="w-12 h-12 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center mb-6" data-id="element-3550">
                <Activity size={24} data-id="element-3551" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-3552">
                Real-Time Market Data
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-3553">
                Live ENTSO-E electricity prices and generation mix data across
                major European markets.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100" data-id="element-3554">
              <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center mb-6" data-id="element-3555">
                <BarChart2 size={24} data-id="element-3556" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-3557">
                Regional Intelligence
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-3558">
                Comprehensive coverage for Germany, Spain, UK, and France with
                granular nodal data.
              </p>
            </div>
          </div>
        </section>

        {/* Feature Rows */}
        <section className="py-16 px-6 max-w-6xl mx-auto space-y-32" data-id="element-3559">
          {/* Row 1 */}
          <div className="flex flex-col md:flex-row items-center gap-16" data-id="element-3560">
            <div className="flex-1" data-id="element-3561">
              <Badge variant="pink" className="mb-4 bg-pink-100 text-pink-600 border-none" data-id="element-3562">
                🎯 Accuracy Advantage
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-3563">
                Predict with confidence using EPT-2
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-3564">
                Our proprietary transformer-based model processes petabytes of
                historical weather and market data to generate probabilistic
                forecasts with unmatched accuracy.
              </p>
              <ul className="space-y-4" data-id="element-3565">
                {['94% accuracy vs actuals', '15-minute granular updates', 'Probabilistic confidence intervals'].map(item => <li key={item} className="flex items-center gap-3 text-slate-700 font-medium" data-id="element-3566">
                    <CheckCircle2 className="text-emerald-500" size={20} data-id="element-3567" />
                    {item}
                  </li>)}
              </ul>
            </div>
            <div className="flex-1 w-full" data-id="element-3568">
              <div className="bg-white p-6 rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100" data-id="element-3569">
                <div className="flex justify-between items-center mb-6" data-id="element-3570">
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-600" data-id="element-3571">
                    <Activity size={16} className="text-pink-500" data-id="element-3572" /> Forecast vs
                    Actual
                  </div>
                  <span className="text-xs font-bold text-emerald-500 bg-emerald-50 px-2 py-1 rounded-md" data-id="element-3573">
                    +94.2% Accuracy
                  </span>
                </div>
                <div className="h-48 flex items-end gap-2" data-id="element-3574">
                  {[40, 60, 45, 70, 85, 65, 90, 75, 80, 55, 45].map((h, i) => <div key={i} className="flex-1 bg-pink-200 rounded-t-sm relative group" style={{
                  height: `${h}%`
                }} data-id="element-3575">
                      <div className="absolute inset-x-0 top-0 h-1 bg-pink-500 rounded-t-sm opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-3576" />
                    </div>)}
                </div>
              </div>
            </div>
          </div>

          {/* Row 2 */}
          <div className="flex flex-col md:flex-row-reverse items-center gap-16" data-id="element-3577">
            <div className="flex-1" data-id="element-3578">
              <Badge variant="blue" className="mb-4 bg-blue-100 text-blue-600 border-none" data-id="element-3579">
                🌍 Continental Coverage
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-3580">
                Market intelligence across borders
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-3581">
                Track generation, load, and pricing across all major European
                markets. Identify cross-border arbitrage opportunities instantly
                with our unified dashboard.
              </p>
              <ul className="space-y-4" data-id="element-3582">
                {['Unified data model for all regions', 'Live cross-border flow maps', 'Real-time congestion alerts'].map(item => <li key={item} className="flex items-center gap-3 text-slate-700 font-medium" data-id="element-3583">
                    <CheckCircle2 className="text-blue-500" size={20} data-id="element-3584" />
                    {item}
                  </li>)}
              </ul>
            </div>
            <div className="flex-1 w-full grid grid-cols-2 gap-4" data-id="element-3585">
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100" data-id="element-3586">
                <div className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-2" data-id="element-3587">
                  <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-3588" /> Germany
                  (DE)
                </div>
                <div className="text-3xl font-bold text-slate-900" data-id="element-3589">12.4 GW</div>
                <div className="text-sm text-emerald-500 font-medium mt-1" data-id="element-3590">
                  ↑ 2.1 GW vs Forecast
                </div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100" data-id="element-3591">
                <div className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-2" data-id="element-3592">
                  <div className="w-2 h-2 rounded-full bg-amber-500" data-id="element-3593" /> Spain
                  (ES)
                </div>
                <div className="text-3xl font-bold text-slate-900" data-id="element-3594">8.2 GW</div>
                <div className="text-sm text-red-500 font-medium mt-1" data-id="element-3595">
                  ↓ 0.4 GW vs Forecast
                </div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100" data-id="element-3596">
                <div className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-2" data-id="element-3597">
                  <div className="w-2 h-2 rounded-full bg-purple-500" data-id="element-3598" /> France
                  (FR)
                </div>
                <div className="text-3xl font-bold text-slate-900" data-id="element-3599">45.1 GW</div>
                <div className="text-sm text-slate-400 font-medium mt-1" data-id="element-3600">
                  On Forecast
                </div>
              </div>
              <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 border-dashed flex items-center justify-center text-slate-400 font-medium text-sm flex-col gap-2 cursor-pointer hover:bg-slate-100 transition-colors" data-id="element-3601">
                <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center text-slate-600" data-id="element-3602">
                  +
                </div>
                Add Region
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-24 px-6 max-w-6xl mx-auto" data-id="element-3603">
          <div className="text-center mb-16" data-id="element-3604">
            <h2 className="text-3xl font-bold text-slate-900" data-id="element-3605">
              Trusted by the best
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6" data-id="element-3606">
            {[{
            quote: "Jua's forecasts have completely transformed our intraday trading strategy. The accuracy is simply unmatched.",
            author: 'Elena Rossi',
            role: 'Head of Trading, EnergyOne'
          }, {
            quote: 'The speed at which we receive model updates gives us a crucial edge in volatile markets.',
            author: 'Thomas Weber',
            role: 'Senior Analyst, PowerGrid'
          }, {
            quote: 'Finally, a platform that combines weather accuracy with deep market intelligence. A game changer.',
            author: 'Sarah Jenkins',
            role: 'Director, DataFlux'
          }].map((t, i) => <div key={i} className={`bg-white p-8 rounded-2xl shadow-sm border border-slate-100 ${i === 2 ? 'opacity-50' : ''}`} data-id="element-3607">
                <div className="flex gap-1 mb-6 text-amber-400" data-id="element-3608">
                  {[1, 2, 3, 4, 5].map(star => <Star key={star} size={16} fill="currentColor" data-id="element-3609" />)}
                </div>
                <p className="text-slate-700 font-medium mb-8 leading-relaxed" data-id="element-3610">
                  "{t.quote}"
                </p>
                <div data-id="element-3611">
                  <div className="font-bold text-slate-900 text-sm" data-id="element-3612">
                    {t.author}
                  </div>
                  <div className="text-slate-500 text-xs mt-1" data-id="element-3613">{t.role}</div>
                </div>
              </div>)}
          </div>
        </section>

        {/* Pricing */}
        <section className="py-24 px-6 max-w-5xl mx-auto" data-id="element-3614">
          <div className="text-center mb-16" data-id="element-3615">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-3616">
              Simple, transparent pricing
            </h2>
            <p className="text-slate-600" data-id="element-3617">
              Choose the plan that fits your trading volume.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 items-center" data-id="element-3618">
            {/* Starter */}
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100" data-id="element-3619">
              <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-3620">Starter</h3>
              <div className="flex items-baseline gap-1 mb-4" data-id="element-3621">
                <span className="text-4xl font-extrabold text-slate-900" data-id="element-3622">
                  €499
                </span>
                <span className="text-slate-500" data-id="element-3623">/mo</span>
              </div>
              <p className="text-sm text-slate-600 mb-8" data-id="element-3624">
                For individual traders and small desks.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-3625">
                {['EPT-2 Model Access', 'Hourly Forecasts', '1 Region Included', 'Standard Support'].map(f => <li key={f} className="flex items-center gap-3 text-sm text-slate-700 font-medium" data-id="element-3626">
                    <CheckCircle2 className="text-emerald-500" size={16} data-id="element-3627" /> {f}
                  </li>)}
              </ul>
              <button className="inline-flex items-center justify-center w-full h-9 px-4 text-sm font-semibold rounded-xl bg-white/60 backdrop-blur-xl border border-white/80 text-slate-700 shadow-[0_2px_12px_rgba(148,163,184,0.10),inset_0_1px_0_rgba(255,255,255,0.90)] hover:bg-white/80 hover:border-white/90 hover:shadow-[0_4px_16px_rgba(148,163,184,0.15),inset_0_1px_0_rgba(255,255,255,1)] transition-all" data-id="element-3628">
                Start Free Trial
              </button>
            </div>

            {/* Pro */}
            <div className="bg-white p-8 rounded-3xl shadow-xl shadow-pink-500/10 border-2 border-pink-500 relative transform md:-translate-y-4" data-id="element-3629">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#f0407a] to-[#a855f7] text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-3630">
                Most Popular
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-3631">Pro</h3>
              <div className="flex items-baseline gap-1 mb-4" data-id="element-3632">
                <span className="text-4xl font-extrabold text-slate-900" data-id="element-3633">
                  €1,299
                </span>
                <span className="text-slate-500" data-id="element-3634">/mo</span>
              </div>
              <p className="text-sm text-slate-600 mb-8" data-id="element-3635">
                Advanced tools for professional teams.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-3636">
                {['All Starter Features', '15-min Forecasts', 'All European Regions', 'API Access', 'Priority Support'].map(f => <li key={f} className="flex items-center gap-3 text-sm text-slate-700 font-medium" data-id="element-3637">
                    <CheckCircle2 className="text-emerald-500" size={16} data-id="element-3638" /> {f}
                  </li>)}
              </ul>
              <button className="inline-flex items-center justify-center w-full h-9 px-4 text-sm font-semibold rounded-xl bg-white/60 backdrop-blur-xl border border-white/80 text-slate-700 shadow-[0_2px_12px_rgba(148,163,184,0.10),inset_0_1px_0_rgba(255,255,255,0.90)] hover:bg-white/80 hover:border-white/90 hover:shadow-[0_4px_16px_rgba(148,163,184,0.15),inset_0_1px_0_rgba(255,255,255,1)] transition-all" data-id="element-3639">
                Contact Sales
              </button>
            </div>

            {/* Enterprise */}
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100" data-id="element-3640">
              <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-3641">
                Enterprise
              </h3>
              <div className="flex items-baseline gap-1 mb-4" data-id="element-3642">
                <span className="text-4xl font-extrabold text-slate-900" data-id="element-3643">
                  Custom
                </span>
              </div>
              <p className="text-sm text-slate-600 mb-8" data-id="element-3644">
                For large-scale operations.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-3645">
                {['Unlimited Seats', 'Custom Model Training', 'Dedicated Account Manager', 'SLA Guarantee', 'On-premise Deployment'].map(f => <li key={f} className="flex items-center gap-3 text-sm text-slate-700 font-medium" data-id="element-3646">
                    <CheckCircle2 className="text-emerald-500" size={16} data-id="element-3647" /> {f}
                  </li>)}
              </ul>
              <button className="inline-flex items-center justify-center w-full h-9 px-4 text-sm font-semibold rounded-xl bg-white/60 backdrop-blur-xl border border-white/80 text-slate-700 shadow-[0_2px_12px_rgba(148,163,184,0.10),inset_0_1px_0_rgba(255,255,255,0.90)] hover:bg-white/80 hover:border-white/90 hover:shadow-[0_4px_16px_rgba(148,163,184,0.15),inset_0_1px_0_rgba(255,255,255,1)] transition-all" data-id="element-3648">
                Contact Sales
              </button>
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="py-12 px-6 max-w-5xl mx-auto mb-24" data-id="element-3649">
          <div className="bg-gradient-to-br from-[#0F172A] via-[#1a1035] to-[#0F172A] rounded-3xl p-12 text-center text-white shadow-2xl relative overflow-hidden" data-id="element-3650">
            <div className="absolute inset-0 bg-gradient-to-tr from-pink-500/10 via-transparent to-purple-500/10 pointer-events-none" data-id="element-3651" />
            <div className="relative" data-id="element-3652">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-3653">
                Ready to outperform the market?
              </h2>
              <p className="text-slate-400 mb-8 max-w-2xl mx-auto" data-id="element-3654">
                Join the leading energy traders using Jua for superior
                forecasting accuracy and real-time intelligence.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-3655">
                <button className="inline-flex items-center justify-center h-9 px-8 text-sm font-semibold rounded-xl bg-white/10 backdrop-blur-xl border border-white/20 text-white shadow-[0_2px_12px_rgba(0,0,0,0.20),inset_0_1px_0_rgba(255,255,255,0.10)] hover:bg-white/20 hover:border-white/30 hover:shadow-[0_4px_16px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.15)] transition-all w-full sm:w-auto" data-id="element-3656">
                  Start Free Trial
                </button>
                <button className="inline-flex items-center justify-center h-9 px-8 text-sm font-medium rounded-lg bg-transparent text-slate-300 hover:text-white hover:bg-white/10 transition-all w-full sm:w-auto" data-id="element-3657">
                  Talk to Sales
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-slate-200 py-8 px-6" data-id="element-3658">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-3659">
            <div className="flex items-center gap-2" data-id="element-3660">
              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#f0407a] to-[#a855f7]" data-id="element-3661" />
              <span className="font-bold text-slate-900" data-id="element-3662">Jua</span>
            </div>
            <div className="text-sm text-slate-500" data-id="element-3663">
              © 2026 Jua. All rights reserved.
            </div>
            <div className="flex gap-6 text-sm font-medium text-slate-600" data-id="element-3664">
              <a href="#" className="hover:text-slate-900" data-id="element-3665">
                Documentation
              </a>
              <a href="#" className="hover:text-slate-900" data-id="element-3666">
                Privacy
              </a>
              <a href="#" className="hover:text-slate-900" data-id="element-3667">
                Terms
              </a>
              <a href="#" className="hover:text-slate-900" data-id="element-3668">
                Contact
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>;
}